import { useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, Star, XCircle, ImageIcon, Download } from "lucide-react";
import { mediaUrl, triggerDownload } from "@/lib/galleryApi";
import { RetryImg } from "@/components/gallery/RetryImg";

const fmtDate = (d) => {
  const m = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2})/.exec(d || "");
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : d;
};

export function AdminLightbox({ photos, index, setIndex, coverId, onFavorite, onReject, onCover }) {
  const p = photos[index];
  const close = () => setIndex(null);
  const nav = useCallback((d) => setIndex((i) => Math.max(0, Math.min(photos.length - 1, i + d))), [photos.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowLeft") nav(-1);
      if (e.key === "ArrowRight") nav(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, setIndex]);

  if (!p) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="photo-preview-lightbox">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-xs text-[#94A3B8] truncate">{p.filename} · {p.width}×{p.height} · {index + 1}/{photos.length}</p>
          {p.client_favorites?.length > 0 && (
            <p className="text-[11px] text-[#F43F5E] truncate" data-testid="lightbox-client-favorites">Favori client : {p.client_favorites.map((f) => f.name).join(", ")}</p>
          )}
          {p.exif && Object.keys(p.exif).length > 0 && (
            <p className="text-[11px] text-[#64748B] truncate" data-testid="lightbox-exif">
              {[p.exif.camera_model, p.exif.lens, p.exif.focal_length && `${p.exif.focal_length} mm`, p.exif.aperture && `f/${p.exif.aperture}`, p.exif.exposure_time, p.exif.iso && `ISO ${p.exif.iso}`, fmtDate(p.exif.date_taken)].filter(Boolean).join(" · ")}
              {p.exif.had_gps && <span className="ml-2 text-amber-400">GPS present dans l'original (retire des fichiers publics)</span>}
            </p>
          )}
        </div>
        <button onClick={close} className="text-white/70 hover:text-white" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center min-h-0 px-12">
        <RetryImg key={p.id} src={mediaUrl(p.web_url)} className="max-w-full max-h-full object-contain" />
        {index > 0 && <button onClick={() => nav(-1)} className="absolute left-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="lightbox-prev"><ChevronLeft /></button>}
        {index < photos.length - 1 && <button onClick={() => nav(1)} className="absolute right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="lightbox-next"><ChevronRight /></button>}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 p-4">
        <Button size="sm" onClick={() => onFavorite(p)} className={`h-8 text-xs ${p.is_favorite ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-white/10 text-white"}`} data-testid="lightbox-favorite"><Star className={`w-3.5 h-3.5 mr-1 ${p.is_favorite ? "fill-[#F59E0B]" : ""}`} />Favori</Button>
        <Button size="sm" onClick={() => onReject(p)} className={`h-8 text-xs ${p.is_rejected ? "bg-red-500/20 text-red-400" : "bg-white/10 text-white"}`} data-testid="lightbox-reject"><XCircle className="w-3.5 h-3.5 mr-1" />{p.is_rejected ? "Rejetee" : "Rejeter"}</Button>
        <Button size="sm" onClick={() => onCover(p)} disabled={coverId === p.id} className="h-8 text-xs bg-white/10 text-white" data-testid="lightbox-cover"><ImageIcon className="w-3.5 h-3.5 mr-1" />{coverId === p.id ? "Couverture" : "Definir en couverture"}</Button>
        <Button size="sm" onClick={() => triggerDownload(mediaUrl(p.download_url))} className="h-8 text-xs bg-[#0099CC] text-white" data-testid="lightbox-download"><Download className="w-3.5 h-3.5 mr-1" />Telecharger</Button>
      </div>
    </div>
  );
}
